import type { ReactNode, RefObject } from "react"
import { useEffect, useRef } from "react"

import { cx } from "../lib/cx"
import styles from "./popover.module.css"

/**
 * Закрытие всплывающего слоя.
 *
 * Два способа закрыть обязательны оба. Escape — потому что администратор работает
 * с клавиатуры и не должен тянуться к мыши, чтобы убрать список филиалов, открытый
 * по ошибке. Нажатие мимо — потому что человек, который передумал, целится не в крестик,
 * а в то место, куда шёл дальше.
 *
 * Слушается pointerdown, а не click: клик приходит ПОСЛЕ отпускания кнопки, и к этому
 * моменту нажатие уже успевает сработать на элементе под слоем.
 */
function useDismiss(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
): void {
  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent): void => {
      const node = ref.current
      if (node === null) return
      if (event.target instanceof Node && node.contains(event.target)) return
      onClose()
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        // Escape не уходит выше: он закрывает верхний слой, а не всё сразу.
        event.stopPropagation()
        onClose()
      }
    }

    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [ref, open, onClose])
}

/**
 * Всплывающий слой рядом с кнопкой.
 *
 * ПОЧЕМУ ЭТО НЕ АДРЕС. ТЗ §4 запрещает делать модальным окном без адреса ДЛИННЫЕ
 * сценарии — те, из которых человек выходит с результатом и к которым возвращается
 * по ссылке. Выбор филиала и меню профиля длинными не являются: они закрываются
 * тем же движением, что открылись, а их РЕЗУЛЬТАТ — выбранный филиал — как раз
 * попадает в адрес. Заводить адрес самому меню значит засорить историю браузера
 * так, что кнопка «назад» перестанет возвращать к предыдущему дню журнала.
 *
 * Слой встаёт относительно ближайшего контейнера с position: relative — его даёт
 * вызывающий код вместе с кнопкой, чтобы список не уезжал от неё при прокрутке.
 */
export function Popover(props: {
  readonly open: boolean
  readonly onClose: () => void
  readonly label: string
  readonly align?: "start" | "end"
  readonly className?: string | undefined
  readonly children: ReactNode
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, props.open, props.onClose)

  // Фокус переносится внутрь: иначе Tab из открытого списка уходит на следующую
  // кнопку шапки, а список остаётся висеть за спиной.
  useEffect(() => {
    if (props.open) ref.current?.focus()
  }, [props.open])

  if (!props.open) return null

  return (
    <div
      ref={ref}
      className={cx(styles.popover, props.align === "end" ? styles.alignEnd : undefined, props.className)}
      role="dialog"
      aria-label={props.label}
      tabIndex={-1}
    >
      {props.children}
    </div>
  )
}
